/**
 * Pure predicate behind the "press on empty space clears the channel selection"
 * gesture shared by every sEEG viewer (#1090).
 *
 * Empty space is anywhere a press does nothing else. It is NOT empty space when
 * the target is:
 *   - the Preview's trace canvas (`.bf-line-trace-canvas`) or a canvas of the
 *     Metrics plot (`.bf-analysis-canvas-analysis-content`). It resolves its own click:
 *     toggle the channel under the pointer, or clear on no channel. Doing it there
 *     keeps a drag-pan or box-zoom from clearing (a click needs < 4 px).
 *   - an interactive element (button, link, form field, slider, menu item, …) or
 *     anything marked with `SELECTION_KEEP_ATTR`.
 *   - the channel side panel (`.bf-ui-panel`), which acts on the selection.
 *   - the EEG canvas context menu (`.bf-context-menu` or its backdrop).
 *   - any dropdown menu overlay (`.bf-dropdown-menu-overlay`), because the
 *     channel panel's three-dot menu is rendered into `document.body` via a
 *     portal.
 *   - the medical viewer (`.medical-viewer`), which is the consumer of the
 *     EEG selection set via #369's orange highlight. A press inside it (rotating
 *     the 3D view, clicking a 2D slice) must not drop the highlight mid-interaction.
 *   - any floating layer marked `.bf-floating-layer` (see below).
 *   - the side menu (`.bf-ui-side-menu`), which holds the control that shows
 *     and hides the channel panel itself (#837 R30).
 *   - the Preview and Analysis tool gutters, background included (#1100): they
 *     hold the Fullscreen buttons.
 *   - a Metrics bar control box (`.bf-metrics-box`), padding included, and the
 *     Normalization and frequency-range controls, titles included (#1154 V78).
 *   - a scrollbar, of the page or of any scrollable element (found by geometry,
 *     see `isScrollbarPress`).
 *
 * Until #1090 the whole EEG chart wrapper also counted as "not empty". In the
 * maximized EEG that wrapper fills the screen, so a clinician had almost nowhere
 * to click to deselect. The time axis, the gaps of the annotation ribbon and the
 * letterbox around the canvas are empty space now.
 */
/**
 * Marker every floating layer portaled out of the viewport tree carries — popovers,
 * dialogs, portaled panels, floating control clusters. Put it on the layer's outermost
 * node (and on its backdrop or overlay, which is a sibling rather than a child)
 * whenever you add one.
 */
export const FLOATING_LAYER_CLASS = 'bf-floating-layer';

/**
 * Opt-out for a clickable element that is not a native control (e.g. a `<span>`
 * with an `onClick`, like an annotation ribbon chip): a press on it is not empty
 * space.
 */
export const SELECTION_KEEP_ATTR = 'data-selection-keep';

const INTERACTIVE_SELECTOR = [
    'button', 'a[href]', 'input', 'select', 'textarea', 'label', '[contenteditable]',
    '[role="button"]', '[role="slider"]', '[role="menuitem"]', '[role="option"]', '[role="tab"]',
    // Not `[role="listbox"]`: its options are the controls, and the blank part of a list
    // below them (the side panel's annotation list) is empty space (#1100, #1090 QA).
    '[role="checkbox"]', '[role="radio"]', '[role="switch"]', '[role="dialog"]',
    `[${SELECTION_KEEP_ATTR}]`,
].join(', ');

const SELECTION_SURFACE_SELECTOR = [
    '.bf-line-trace-canvas',
    // The Metrics plot's canvases (#1109), which resolve their own click like the trace
    // canvas. Not the whole plot box: the gaps of its annotation ribbon are empty space.
    '.bf-analysis-canvas-analysis-content canvas',
    '.bf-ui-panel',
    '.bf-ui-side-menu',
    '.medical-viewer',
    '.bf-context-menu',
    '.bf-context-menu-backdrop',
    '.bf-dropdown-menu-overlay',
    // The tool gutters hold the Fullscreen buttons; #1100 keeps the selection on the whole
    // gutter, background included, so entering or leaving fullscreen never drops it.
    '.preview-tool-gutter',
    '.analysis-seeg-tool-gutter',
    // A Metrics bar box (the traces' switch + GAIN, the shade's switch + Z-score) is one
    // bordered control: its padding and its switch's eye icons sit right beside the
    // label, so a press there was read as a click away (#1154 V78).
    '.bf-metrics-box',
    // The Normalization switch and the frequency range read as controls of the same bar,
    // their titles included (#1154 V78, re-review).
    '.bf-normalization-widget',
    '.bf-freq-filter',
    // A floating layer is a popover, dialog or panel portaled to `document.body`. Each
    // such layer is a fresh chance to reintroduce #837 F2: the sensitivity popover was
    // opened to act ON the selection, yet pressing its slider was read as a click away
    // and emptied it. Rather than name each instance here, every floating layer carries
    // this marker and the predicate checks it once.
    `.${FLOATING_LAYER_CLASS}`,
].join(', ');

/** Where a press landed, in viewport coordinates. */
export interface PressPoint {
    clientX: number;
    clientY: number;
}

// An overlay scrollbar (macOS with a trackpad) takes no layout room, so its
// position can only be estimated: the band it is drawn in at the element's edge.
const OVERLAY_SCROLLBAR_BAND_PX = 16;

const canScroll = (overflow: string): boolean => overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';

/**
 * The press landed on `el`'s own scrollbar (PR #1121 review, round 2). A press on a
 * scrollbar targets the scrolling element itself, so only geometry tells it from the
 * element's blank area: past the client box, on an axis the element scrolls on. The
 * root's scrollbar is the viewport's, whatever the root's `overflow` says.
 */
function isScrollbarPress(el: Element, { clientX, clientY }: PressPoint): boolean {
    const doc = el.ownerDocument;
    const view = doc.defaultView;
    if (!view) return false;
    const isRoot = el === doc.documentElement;
    const style = view.getComputedStyle(el);
    const box = isRoot ? { left: 0, top: 0 } : el.getBoundingClientRect();
    const x = clientX - box.left - el.clientLeft;
    const y = clientY - box.top - el.clientTop;
    const outerWidth = isRoot ? view.innerWidth : (el as HTMLElement).offsetWidth - 2 * el.clientLeft;
    const outerHeight = isRoot ? view.innerHeight : (el as HTMLElement).offsetHeight - 2 * el.clientTop;
    // A classic scrollbar sits past the client box; an overlay one inside its edge band.
    const edge = (client: number, outer: number) => (outer > client ? client : client - OVERLAY_SCROLLBAR_BAND_PX);

    const scrollsY = el.scrollHeight > el.clientHeight && (isRoot || canScroll(style.overflowY));
    const scrollsX = el.scrollWidth > el.clientWidth && (isRoot || canScroll(style.overflowX));
    return (scrollsY && x >= edge(el.clientWidth, outerWidth))
        || (scrollsX && y >= edge(el.clientHeight, outerHeight));
}

/**
 * `point` is where the press landed. With it, a press on the target's scrollbar is a
 * control press, not empty space; without it that check is skipped.
 */
export function isEmptySpacePress(target: EventTarget | null, point?: PressPoint): boolean {
    // Not `instanceof Element`: a detached viewer's document lives in another
    // window, whose `Element` is a different constructor (#761).
    if (!target || (target as Node).nodeType !== Node.ELEMENT_NODE) {
        return false;
    }
    const el = target as Element;
    return el.closest(INTERACTIVE_SELECTOR) === null
        && el.closest(SELECTION_SURFACE_SELECTOR) === null
        && !(point && isScrollbarPress(el, point));
}

/**
 * Wire the empty-space gesture on the document that shows a viewer: a press on
 * empty space calls `onEmpty`, unless a tool is armed. Returns the cleanup.
 *
 * "Armed" is read when the press starts, in the capture phase, before any target
 * handler runs (PR #1121 review R22). A tool's own handler may complete its action
 * and disarm on that same press (Modify's second time-axis press), and the browser
 * flushes Preact's re-render in the microtask checkpoint between listeners, so a
 * read in the bubble phase would already see it disarmed. The decision itself stays
 * in the bubble phase, so a target that stops the press from bubbling still keeps
 * the selection. Right and middle presses never clear, nor does a press on a scrollbar.
 */
export function listenEmptySpacePress(doc: Document, isArmed: () => boolean, onEmpty: () => void): () => void {
    let armedAtPress = false;
    const snapshot = () => {
        armedAtPress = isArmed();
    };
    const decide = (e: MouseEvent) => {
        // Only the primary button, as on the trace canvas (#1090 review round 2, Q3).
        if (armedAtPress || e.button !== 0) return;
        if (isEmptySpacePress(e.target, e)) onEmpty();
    };
    doc.addEventListener('mousedown', snapshot, true);
    doc.addEventListener('mousedown', decide);
    return () => {
        doc.removeEventListener('mousedown', snapshot, true);
        doc.removeEventListener('mousedown', decide);
    };
}
